import type { DeliveryRecordRequest, DeliveryRecordResult } from '@/lib/shipments/delivery-records';
import { supabase } from '@/lib/supabase/client';
import type { ShipmentMergeStrategy } from '@/lib/shipments/merge-candidates';

export {
  findShipmentMergeCandidates,
  type ShipmentMergeCandidate,
  type ShipmentMergeStrategy,
} from '@/lib/shipments/merge-candidates';

async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

export type ShipmentCreationSource = 'manual' | 'purchase_order_fulfillment' | 'script' | 'migration' | 'unknown';

export interface Shipment {
  id: string;
  location_id: string;
  launch_id: string | null;
  purchase_order_id: string | null;
  delivery_intent?: string;
  records_delivery_only?: boolean;
  creation_source: ShipmentCreationSource;
  created_by: string | null;
  updated_by: string | null;
  merged_into_shipment_id?: string | null;
  merged_at?: string | null;
  merged_by?: string | null;
  shipment_date: string;
  status: 'pending' | 'partially_shipped' | 'shipped' | 'delivered' | 'returned' | 'cancelled';
  tracking_number: string | null;
  carrier: string | null;
  shipment_source: string;
  estimated_delivery_date: string | null;
  actual_delivery_date: string | null;
  ship_to_address: string | null;
  notes: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface ShipmentInsert {
  location_id: string;
  launch_id?: string | null;
  purchase_order_id?: string | null;
  creation_source?: ShipmentCreationSource;
  shipment_date: string;
  status?: 'pending' | 'partially_shipped' | 'shipped' | 'delivered' | 'returned' | 'cancelled';
  tracking_number?: string | null;
  carrier?: string | null;
  shipment_source: string;
  estimated_delivery_date?: string | null;
  actual_delivery_date?: string | null;
  ship_to_address?: string | null;
  notes?: string | null;
}

export interface ShipmentUpdate {
  location_id?: string;
  launch_id?: string | null;
  purchase_order_id?: string | null;
  creation_source?: ShipmentCreationSource;
  shipment_date?: string;
  status?: 'pending' | 'partially_shipped' | 'shipped' | 'delivered' | 'returned' | 'cancelled';
  tracking_number?: string | null;
  carrier?: string | null;
  shipment_source?: string;
  estimated_delivery_date?: string | null;
  actual_delivery_date?: string | null;
  ship_to_address?: string | null;
  notes?: string | null;
}

export interface ShipmentMergeResult {
  canonical_shipment_id: string;
  archived_shipment_id: string;
  item_actions: number;
  strategy: ShipmentMergeStrategy;
}

export const shipmentsService = {
  async mutateDeliveryRecord(request: DeliveryRecordRequest): Promise<DeliveryRecordResult> {
    const { data, error } = await supabase.rpc('mutate_delivery_record', request);
    if (error) throw new Error(error.message);
    if (!data) throw new Error('Delivery save returned no result');
    return data as DeliveryRecordResult;
  },
  async updatePurchaseOrderReceiptStatus(shipment: Shipment, status: Shipment['status']): Promise<void> {
    const {data,error} = await supabase.from('shipments').update({status}).eq('id',shipment.id)
      .eq('purchase_order_id',shipment.purchase_order_id).eq('location_id',shipment.location_id)
      .eq('creation_source','purchase_order_fulfillment').eq('updated_at',shipment.updated_at)
      .is('merged_into_shipment_id',null).not('status','in','(returned,cancelled)').select('id').maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('PO shipment changed during status refresh. Reload before saving again.');
  },
  async syncPurchaseOrderReceiptItems(purchaseOrderId: string, shipmentId: string, items: {component_id: string; quantity: number; carrier: string | null; estimated_delivery_date: string | null}[]): Promise<void> {
    const {error} = await supabase.rpc('sync_purchase_order_receipt_items', {p_purchase_order_id: purchaseOrderId,p_shipment_id: shipmentId,p_items: items});
    if (error) throw new Error(error.message);
  },
  async fillPurchaseOrderLaunch(id: string, purchaseOrderId: string, locationId: string, launchId: string | null): Promise<Shipment> {
    const { data, error } = await supabase.from('shipments').update({launch_id: launchId})
      .eq('id', id).eq('purchase_order_id', purchaseOrderId).eq('location_id', locationId).is('launch_id', null)
      .eq('creation_source', 'purchase_order_fulfillment').is('merged_into_shipment_id', null).not('status','in','(returned,cancelled)').select().maybeSingle();
    if (error) throw error;
    if (data) return data as Shipment;
    const current = await this.getById(id);
    if (!current || current.purchase_order_id !== purchaseOrderId || current.location_id !== locationId || current.launch_id !== launchId || current.creation_source !== 'purchase_order_fulfillment' || current.merged_into_shipment_id || ['returned','cancelled'].includes(current.status)) {
      throw new Error('PO shipment launch changed. Review its attribution before saving again.');
    }
    return current;
  },

  async getAll(): Promise<Shipment[]> {
    try {
      const { data, error } = await supabase.from('shipments').select('*').order('shipment_date', { ascending: false });

      if (error) {
        console.error('Error fetching shipments:', error);
        return [];
      }

      return (data as Shipment[]) || [];
    } catch (error) {
      console.error('Error fetching shipments:', error);
      return [];
    }
  },

  async getById(id: string): Promise<Shipment | null> {
    try {
      const { data, error } = await supabase.from('shipments').select('*').eq('id', id).single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching shipment:', error);
        return null;
      }

      return (data as Shipment) || null;
    } catch (error) {
      console.error('Error fetching shipment:', error);
      return null;
    }
  },

  async getByLocationId(locationId: string): Promise<Shipment[]> {
    try {
      const { data, error } = await supabase
        .from('shipments')
        .select('*')
        .eq('location_id', locationId)
        .order('shipment_date', { ascending: false });

      if (error) {
        console.error('Error fetching shipments by location ID:', error);
        return [];
      }

      return (data as Shipment[]) || [];
    } catch (error) {
      console.error('Error fetching shipments by location ID:', error);
      return [];
    }
  },

  async getByPurchaseOrderId(purchaseOrderId: string): Promise<Shipment | null> {
    try {
      const { data, error } = await supabase
        .from('shipments')
        .select('*')
        .eq('purchase_order_id', purchaseOrderId)
        .neq('status', 'cancelled')
        .is('merged_into_shipment_id', null)
        .eq('creation_source', 'purchase_order_fulfillment')
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching shipment by purchase order ID:', error);
        return null;
      }

      return (data as Shipment) || null;
    } catch (error) {
      console.error('Error fetching shipment by purchase order ID:', error);
      return null;
    }
  },

  async getCanonicalByPurchaseOrderId(purchaseOrderId: string): Promise<Shipment | null> {
    const active = await this.getByPurchaseOrderId(purchaseOrderId);
    if (active) return active;
    const { data, error } = await supabase.from('shipments').select('*')
      .eq('purchase_order_id', purchaseOrderId).not('merged_into_shipment_id', 'is', null)
      .order('created_at', { ascending: false }).limit(1);
    if (error) throw error;
    let candidate = data?.[0] as Shipment | undefined;
    const visited = new Set<string>();
    while (candidate?.merged_into_shipment_id && !visited.has(candidate.id)) {
      visited.add(candidate.id);
      candidate = (await this.getById(candidate.merged_into_shipment_id)) || undefined;
    }
    return candidate && candidate.status !== 'cancelled' && !candidate.merged_into_shipment_id ? candidate : null;
  },

  async getByLaunchId(launchId: string): Promise<Shipment[]> {
    try {
      const { data, error } = await supabase
        .from('shipments')
        .select('*')
        .eq('launch_id', launchId)
        .order('shipment_date', { ascending: false });

      if (error) {
        console.error('Error fetching shipments by launch ID:', error);
        return [];
      }

      return (data as Shipment[]) || [];
    } catch (error) {
      console.error('Error fetching shipments by launch ID:', error);
      return [];
    }
  },

  async create(data: ShipmentInsert): Promise<Shipment> {
    try {
      const userId = await getCurrentUserId();
      const insertData = {
        location_id: data.location_id,
        launch_id: data.launch_id ?? null,
        purchase_order_id: data.purchase_order_id ?? null,
        creation_source: data.creation_source ?? 'manual',
        shipment_date: data.shipment_date,
        status: data.status ?? 'shipped',
        tracking_number: data.tracking_number ?? null,
        carrier: data.carrier ?? null,
        shipment_source: data.shipment_source,
        estimated_delivery_date: data.estimated_delivery_date ?? null,
        actual_delivery_date: data.actual_delivery_date ?? null,
        ship_to_address: data.ship_to_address ?? null,
        notes: data.notes ?? null,
        created_by: userId,
        updated_by: userId,
      };

      const { data: newShipment, error } = await supabase.from('shipments').insert(insertData).select().single();

      if (error) {
        console.error('Error creating shipment:', error);
        throw new Error(`Failed to create shipment: ${error.message}`);
      }

      return newShipment as Shipment;
    } catch (error) {
      console.error('Error creating shipment:', error);
      throw error;
    }
  },

  async update(id: string, data: ShipmentUpdate): Promise<Shipment> {
    try {
      const userId = await getCurrentUserId();
      const updateData: Record<string, unknown> = {};

      // Only include fields that are provided
      if (data.location_id !== undefined) updateData.location_id = data.location_id;
      if (data.launch_id !== undefined) updateData.launch_id = data.launch_id;
      if (data.purchase_order_id !== undefined) updateData.purchase_order_id = data.purchase_order_id;
      if (data.creation_source !== undefined) updateData.creation_source = data.creation_source;
      if (data.shipment_date !== undefined) updateData.shipment_date = data.shipment_date;
      if (data.status !== undefined) updateData.status = data.status;
      if (data.tracking_number !== undefined) updateData.tracking_number = data.tracking_number;
      if (data.carrier !== undefined) updateData.carrier = data.carrier;
      if (data.shipment_source !== undefined) updateData.shipment_source = data.shipment_source;
      if (data.estimated_delivery_date !== undefined) updateData.estimated_delivery_date = data.estimated_delivery_date;
      if (data.actual_delivery_date !== undefined) updateData.actual_delivery_date = data.actual_delivery_date;
      if (data.ship_to_address !== undefined) updateData.ship_to_address = data.ship_to_address;
      if (data.notes !== undefined) updateData.notes = data.notes;
      updateData.updated_by = userId;

      const { data: updatedShipment, error } = await supabase
        .from('shipments')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating shipment:', error);
        throw new Error(`Failed to update shipment: ${error.message}`);
      }

      if (!updatedShipment) {
        throw new Error('Shipment not found');
      }

      return updatedShipment as Shipment;
    } catch (error) {
      console.error('Error updating shipment:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase.from('shipments').delete().eq('id', id);

      if (error) {
        console.error('Error deleting shipment:', error);
        throw new Error(`Failed to delete shipment: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting shipment:', error);
      throw error;
    }
  },

  async mergeDuplicate(
    canonicalShipmentId: string,
    duplicateShipmentId: string,
    strategy: ShipmentMergeStrategy
  ): Promise<ShipmentMergeResult> {
    const { data, error } = await supabase.rpc('merge_duplicate_shipments', {
      p_canonical_shipment_id: canonicalShipmentId,
      p_duplicate_shipment_id: duplicateShipmentId,
      p_strategy: strategy,
    });

    if (error) {
      throw new Error(`Failed to merge shipments: ${error.message}`);
    }

    return data as ShipmentMergeResult;
  },
};
